<template>
  <div>
    <!-- Filter Title -->
    <div
      class="flex items-center justify-between w-full h-[50px] bg-black border-b-[3px] border-b-empire-yellow cursor-pointer"
      @click="toggleExpanded"
    >
      <h3 class="font-archivo-narrow font-semibold text-white px-4 uppercase">{{ rootFilter.name }}</h3>
      <div class="flex items-center">
        <svg
          class="w-5 h-5 text-white mr-4 transform"
          :class="{ 'rotate-180': isExpanded }"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
    </div>

    <!-- Filter Content -->
    <div v-show="isExpanded" class="border border-[#FAFAFA] p-4">
      <!-- Search bar - hidden when any checkbox is checked -->
      <div class="mb-3" v-if="!hasSelectedCheckboxes">
        <div class="flex items-center justify-between gap-0 border-2 border-black/70 h-16 pl-2 pr-0 w-full">
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('categoryPage.dynamicFilters.searchPlaceholder')"
            class="flex-1 px-2 outline-none font-archivo text-[15px] text-black/70 bg-transparent"
          />
          <button
            type="button"
            class="w-[58px] h-full bg-empire-yellow flex items-center justify-center"
            aria-label="Pesquisar"
          >
            <svg class="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="7" stroke="#000" stroke-width="2" />
              <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="#000" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Option list -->
      <div class="space-y-2">
        <div v-if="paginatedChildren.length === 0 && searchQuery" class="text-sm text-black/50 font-archivo">
          {{ $t('categoryPage.dynamicFilters.noResultsFound') }}
        </div>

        <label
          v-for="child in paginatedChildren"
          :key="child.id"
          :class="['flex items-center gap-3 cursor-pointer select-none', disabled ? 'opacity-60 cursor-wait' : '']"
        >
          <input
            type="checkbox"
            :value="child.id"
            :checked="isSelected(child.id)"
            @change="toggleSelection(child.id)"
            class="w-4 h-4 flex-shrink-0 border border-black/40"
            :disabled="disabled"
          />
          <span class="font-archivo text-[15px] text-black/70">
            {{ child.name }}
            <span v-if="child.productCount !== undefined && child.productCount > 0" class="text-black/50">
              ({{ child.productCount }})
            </span>
          </span>
        </label>

        <!-- Show More / Show Less buttons -->
        <div v-if="filteredChildren.length > itemsPerPage && !searchQuery" class="py-2 flex flex-col gap-2">
          <button
            v-if="visibleCount < filteredChildren.length"
            @click="visibleCount += itemsPerPage"
            class="text-empire-yellow font-archivo text-[14px] hover:underline text-left"
          >
            {{ $t('categoryPage.dynamicFilters.showMore') }} (+{{ Math.min(itemsPerPage, filteredChildren.length - visibleCount) }})
          </button>
          <button
            v-if="visibleCount > itemsPerPage"
            @click="visibleCount = itemsPerPage"
            class="text-empire-yellow font-archivo text-[14px] hover:underline text-left"
          >
            {{ $t('categoryPage.dynamicFilters.showLess') }}
          </button>
        </div>

        <div v-if="rootFilter.children && rootFilter.children.length === 0" class="text-sm text-black/50 font-archivo">
          {{ $t('categoryPage.dynamicFilters.noOptionsAvailable') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed } from 'vue'

const props = defineProps({
  rootFilter: {
    type: Object,
    required: true
  },
  selectedFilters: {
    type: Array,
    default: () => []
  },
  productCounts: {
    type: Object,
    default: () => ({})
  },
  showSearch: {
    type: Boolean,
    default: true
  },
  initialExpanded: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:selectedFilters'])

const isExpanded = ref(props.initialExpanded)
const searchQuery = ref('')
const visibleCount = ref(10)
const itemsPerPage = 10

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

// Computed to check whether any checkbox of this subcategory is checked
const hasSelectedCheckboxes = computed(() => {
  if (!props.rootFilter.children || props.rootFilter.children.length === 0) return false
  
  const childIds = props.rootFilter.children.map(child => child.id)
  return props.selectedFilters.some(filterId => childIds.includes(filterId))
})

const filteredChildren = computed(() => {
  if (!props.rootFilter.children) return []

  if (!searchQuery.value) return props.rootFilter.children

  const normalize = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  const q = normalize(searchQuery.value)

  return props.rootFilter.children.filter(child =>
    normalize(child.name).includes(q)
  )
})

const paginatedChildren = computed(() => {
  if (searchQuery.value) {
    // If there is a search, show every result
    return filteredChildren.value
  }

  return filteredChildren.value.slice(0, visibleCount.value)
})

const isSelected = (filterId) => {
  return props.selectedFilters.includes(filterId)
}

const toggleSelection = (filterId) => {
  const newSelection = [...props.selectedFilters]
  const index = newSelection.indexOf(filterId)

  if (index > -1) {
    newSelection.splice(index, 1)
  } else {
    newSelection.push(filterId)
  }

  emit('update:selectedFilters', newSelection)
}
</script>

<style scoped>
/* Specific styles when needed */
</style>


